<template>
    <div v-if="json" class="block-internship-benefits-perks">
        <div class="capper">
            <div class="capper-padded-2x">
                <div class="items">
                    <div class="items-l">
                        <h2 ref="benefitsH2">{{ json.internshipTitle }}</h2>
                        <div class="caption" v-html="json.internshipCaption"></div>
                    </div>
                    <div class="items-r">
                        <div class="bg-panel" ref="bgPanel"></div>
                        <img alt="Internship" ref="internshipImage" :src="json?.internshipGraphic?.[0].url" />
                    </div>
                </div>
                <Lines type="blank" />
                <div class="items2">
                    <h2 ref="internshipH2">{{ json.benefitsPerksTitle }}</h2>
                    <div class="caption" v-html="json.benefitsPerksCaption"></div>
                    <div class="btn-outer" ref="btnOuter">
                        <Link
                            v-if="json?.linkout?.url"
                            :href="json?.linkout?.url"
                            :target="json?.linkout?.target"
                            :label="gh.removeTags(json?.linkout?.label)"
                            :isButton="true"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import gsap from '@/plugins/gsap';
import SplitText from '@/plugins/gsap/SplitText';
import gh from '@/helpers/gh';
import Link from "@/components/pieces/Link.vue";
import Lines from "@/components/pieces/Lines.vue";

gsap.registerPlugin(SplitText);

export default {
    name: "InternshipBenefitsPerks",
    components: { Link, Lines },
    props: {
        json: { type: Object, required: true },
    },
    data() {
        return {
            gh,
            observer: null,
            animationTriggered: false,
            benefitsSplit: null,
            internshipSplit: null,
        };
    },
    mounted() {
        // Do the text splitting earlier in the lifecycle
        this.splitHeadings();
        this.initSetup();
        this.setupAnimationObserver();
    },
    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        // Split the h2 elements as soon as possible and store the split instances.
        splitHeadings() {
            // Split the first h2
            const benefitsH2 = this.$refs.benefitsH2;
            if (benefitsH2) {
                this.benefitsSplit = new SplitText(benefitsH2, { type: "words" });
            }
            // Split the second h2
            const internshipH2 = this.$refs.internshipH2;
            if (internshipH2) {
                this.internshipSplit = new SplitText(internshipH2, { type: "words" });
            }
        },
        // Set initial states for captions, bg-panel, image and btn-outer.
        initSetup() {
            // Set initial state for all SplitText words for h2s
            if (this.benefitsSplit) {
                gsap.set(this.benefitsSplit.words, { opacity: 0, y: 15 });
            }
            if (this.internshipSplit) {
                gsap.set(this.internshipSplit.words, { opacity: 0, y: 15 });
            }
            // Initialize captions: hide them
            const captions = this.$el.querySelectorAll(".caption");
            captions.forEach(caption => {
                gsap.set(caption, { opacity: 0, y: 15 });
            });
            // Set initial state for bg-panel, image and btn-outer
            const bgPanelEl = this.$refs.bgPanel;
            const imageEl = this.$refs.internshipImage;
            const btnOuterEl = this.$refs.btnOuter;
            [bgPanelEl, imageEl, btnOuterEl].forEach(el => {
                if (el) {
                    gsap.set(el, {
                        opacity: 0
                    });
                }
            });
        },
        // Observer to trigger all animations when the component is in view
        setupAnimationObserver() {
            this.observer = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !this.animationTriggered) {
                        this.animationTriggered = true;
                        this.animate();
                        observer.disconnect();
                    }
                });
            }, { threshold: 0.35 });
            this.observer.observe(this.$el);
        },
        // Animate h2 words, captions, bg-panel, image and btn-outer separately.
        animate() {
            const timeline = gsap.timeline();

            // Animate benefits h2 words
            if (this.benefitsSplit) {
                timeline.fromTo(this.benefitsSplit.words,
                    { opacity: 0, y: 15 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.75,
                        ease: "power2.out",
                        stagger: 0.1,
                    },
                    0
                );
            }

            // Animate internship h2 words
            if (this.internshipSplit) {
                timeline.fromTo(this.internshipSplit.words,
                    { opacity: 0, y: 15 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.75,
                        ease: "power2.out",
                        stagger: 0.1,
                    },
                    0
                );
            }

            // Animate captions (both captions are animated together)
            const captions = this.$el.querySelectorAll(".caption");
            captions.forEach(caption => {
                timeline.fromTo(caption,
                    { opacity: 0, y: 15 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.75,
                        ease: "power2.out",
                    },
                    0.2
                );
            });

            // Animate btn-outer separately
            const btnOuterEl = this.$refs.btnOuter;
            if (btnOuterEl) {
                timeline.to(btnOuterEl, {
                    opacity: 1,
                    x: 0,
                    y: 0,
                    duration: 0.7,
                    ease: "power2.out",
                }, 0.4);
            }

            // Animate bg-panel separately
            const bgPanelEl = this.$refs.bgPanel;
            if (bgPanelEl) {
                timeline.fromTo(bgPanelEl,
                    {opacity: 0, x: 50, y:50},
                    {
                    opacity: 1,
                    x: 0,
                    y: 0,
                    duration: 0.3,
                    ease: "power2.out",
                }, 0);
            }

            // Animate image separately
            const imageEl = this.$refs.internshipImage;
            if (imageEl) {
                timeline.fromTo(imageEl,
                    {opacity: 0, x: -50, y:-50},
                    {
                    opacity: 1,
                    x: 0,
                    y: 0,
                    duration: 0.3,
                    ease: "power2.out",
                }, 0.2);
            }


        },
    }
};
</script>
